import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import { motion } from "framer-motion";

import { paper, fonts, shade, inkA, gloss } from "../theme";
import { MsDisplay, MaskHeadline } from "../ms";
import { CARD_RATIO, CARD_RADIUS, cardTransition } from "./promptCard";
import { PromptCardBack } from "./PromptCardBack";

/*
 * The drawn card, turned face-up.
 *
 * The prompt landing is one moment in the round, and it looks the same however
 * the prompt was chosen — whether the picker typed a code off a card in their
 * hand or turned one of three on screen. So it's one component, not a shape each
 * phase draws for itself. The deckless screen had grown its own near-copy: a
 * different paper, a different radius, its own prompt type. Two reveals is one
 * reveal too many, and the difference showed.
 *
 * The caller says which element grows into it. The physical phase has one card
 * and leaves it at the default; the deckless phase has a hand, so it names the
 * card that was actually turned. The inner text carries `layoutId="prompt-text"`,
 * which is what the word picker's prompt morphs from in the next phase.
 */

/* The printed surface of a face-up card. Sits on `RevealCard` normally, and on
 * the front face when the card turns — a card that flips has to take its own
 * paper with it, or the surface stays put while the print rotates off it. */
const cardSurface = css`
    background: ${paper.paperCool};
    border-radius: ${CARD_RADIUS};
    box-shadow:
        0 1px 0 ${gloss(0.6)} inset,
        0 0 0 1px ${shade(0.07)},
        0 14px 30px -12px ${shade(0.42)},
        0 30px 46px -24px ${shade(0.48)};
`;

const RevealCard = styled(motion.div)<{ flip?: boolean }>`
    position: relative;
    /* Depth for the turn. Harmless when nothing flips, and not a transform, so it
       doesn't disturb the layout projection. */
    perspective: 1400px;
    width: 100%;
    max-width: 380px;
    margin: 0 auto;
    aspect-ratio: ${CARD_RATIO};
    ${p => p.flip ? "" : cardSurface}
    ${p => p.flip ? "" : "overflow: hidden;"}
`;

/** The inset printed border. Shared with the picker's face-up sample card. */
export const CardBorderFrame = styled.div`
    position: absolute;
    inset: 9px;
    border: 1.4px solid ${inkA(0.5)};
    border-radius: 6px;
    pointer-events: none;
`;

/** The faint red quote mark behind the type. Also on the picker's sample card. */
export const CardGlyph = styled.div`
    position: absolute;
    top: -0.12em;
    left: 0.02em;
    font-family: ${fonts.serif};
    font-style: italic;
    font-weight: 900;
    font-size: 190px;
    line-height: 1;
    color: ${paper.red};
    opacity: 0.09;
    pointer-events: none;
`;

const RevealArea = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 34px 30px;
    text-align: center;
    z-index: 1;
`;

const RevealPrompt = styled(MsDisplay)`
    font-size: 26px;
    line-height: 1.14;
`;

const RevealRule = styled(motion.div)`
    width: 44px;
    height: 2px;
    border-radius: 1px;
    background: ${paper.ink};
    margin-top: 18px;
    transform-origin: center;
`;

const PromptTextLayer = styled(motion.div)`
    position: relative;
`;

/*
 * The turn-over.
 *
 * `Flipper` sits *inside* the element carrying the `layoutId`, for the same
 * reason the fan does in the hand: framer writes its own transform on the
 * layoutId element to morph it, and a rotateY declared there fights that. Inside,
 * the two are free to compose — the card grows into place and turns over at the
 * same time.
 *
 * The faces are the real thing on both sides: the printed back is the actual
 * `PromptCardBack`, so a card that flips is the same card, not a red rectangle
 * standing in for one.
 */
const flipIn = keyframes`
    from { transform: rotateY(180deg); }
    to   { transform: rotateY(0deg); }
`;

/* A CSS animation, not a framer one. Framer wrote the `initial` and then never
 * ran the `animate` — inside an element it's mid-projecting for a `layoutId`
 * morph, the enter animation doesn't fire, and the card sat on its back face
 * forever. A keyframe is immune to that, and `prefers-reduced-motion` is already
 * handled globally in index.css by collapsing durations, which lands this on its
 * final frame: face up, which is the correct resting state. */
const Flipper = styled.div`
    position: absolute;
    inset: 0;
    transform-style: preserve-3d;
    animation: ${flipIn} 0.66s cubic-bezier(0.34, 0, 0.18, 1) both;
`;

const FaceBase = styled.div`
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    border-radius: ${CARD_RADIUS};
    overflow: hidden;
`;

/* The front carries the paper; the back is a real `PromptCardBack`, which brings
 * its own. */
const Face = styled(FaceBase)`
    ${cardSurface}
`;

const FaceBack = styled(FaceBase)`
    transform: rotateY(180deg);
`;

/**
 * @param flip Turn the card over as it arrives, back to front. For the deckless
 *   draw, where what you were looking at a moment ago *was* the back of this
 *   card. The physical phase leaves it off: its picker is already looking at a
 *   face-up card, so flipping one in would be a card turning over twice.
 */
export function PromptReveal({ promptText, layoutId = "prompt-card", flip = false }: {
    promptText: string;
    layoutId?: string;
    flip?: boolean;
}) {
    const face = (
        <>
            <CardGlyph>"</CardGlyph>
            <CardBorderFrame />
            <RevealArea>
                <PromptTextLayer layoutId="prompt-text">
                    <RevealPrompt>
                        {/* Held back until the card is past edge-on — the type
                            shouldn't start setting itself on a face nobody can
                            see yet. */}
                        <MaskHeadline delay={flip ? 0.46 : 0.28}>
                            {promptText.replace(/___/g, "_____")}
                        </MaskHeadline>
                    </RevealPrompt>
                </PromptTextLayer>
                <RevealRule
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{
                        opacity: 1,
                        scaleX: 1,
                        transition: { delay: flip ? 0.68 : 0.5, duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                    }}
                />
            </RevealArea>
        </>
    );

    return (
        <RevealCard key="headline" layoutId={layoutId} transition={cardTransition} flip={flip}>
            {flip ? (
                <Flipper>
                    <Face>{face}</Face>
                    <FaceBack><PromptCardBack /></FaceBack>
                </Flipper>
            ) : face}
        </RevealCard>
    );
}
